import { useEffect, useState } from "react";
import {
  type PricingConfigBody,
  type PricingConfigResponse,
  fetchPricingConfig,
  resetPricingConfig,
  savePricingConfig,
} from "../api";
import {
  getChartResolution,
  getPriceIncludesTax,
  setChartResolution,
  setPriceIncludesTax,
  type ChartResolution,
} from "../prefs";

/**
 * Account section: supplier preset (Zonneplan / Tibber / Frank /
 * Custom) or own tariffs, backed by /api/pricing/config (PUT writes a
 * JSON override on the backend's PVC, DELETE returns to the installer
 * default). The form is the source of truth; a preset is a start.
 * docs/customer-app.md > Pricing config > Supplier presets
 */

interface Preset {
  id: string;
  label: string;
  body: PricingConfigBody;
}

// All preset values EXCL BTW (vat_pct is applied once at the end).
// NL 2026 numbers, back-derived against Zonneplan's GBB tariff dump
// for 2026-05-26: purchase = (epex + 0.025 + 0.0831) × 1.21, sales =
// purchase × (1 + sun_bonus_if_daytime); NL salders through 2026 so
// `export_mode='saldering'` by default.
// docs/customer-app.md > Pricing config > Supplier presets
const PRESETS: Preset[] = [
  {
    id: "zonneplan",
    label: "Zonneplan",
    body: {
      contract: "dynamic",
      fixed_eur_per_kwh: null,
      supplier_margin_eur_per_kwh: 0.025,
      tax_eur_per_kwh: 0.0831,
      vat_pct: 0.21,
      export_mode: "saldering",
      sun_bonus_pct: 0.10,
      export_margin_eur_per_kwh: 0.0,
      feed_in_tariff_eur_per_kwh: 0.08,
    },
  },
  {
    id: "tibber",
    label: "Tibber",
    body: {
      contract: "dynamic",
      fixed_eur_per_kwh: null,
      supplier_margin_eur_per_kwh: 0.018,
      tax_eur_per_kwh: 0.0831,
      vat_pct: 0.21,
      export_mode: "saldering",
      sun_bonus_pct: 0.0,
      export_margin_eur_per_kwh: -0.018,
      feed_in_tariff_eur_per_kwh: 0.08,
    },
  },
  {
    id: "frank",
    label: "Frank Energie",
    body: {
      contract: "dynamic",
      fixed_eur_per_kwh: null,
      supplier_margin_eur_per_kwh: 0.025,
      tax_eur_per_kwh: 0.0831,
      vat_pct: 0.21,
      export_mode: "saldering",
      sun_bonus_pct: 0.0,
      export_margin_eur_per_kwh: 0.0,
      feed_in_tariff_eur_per_kwh: 0.08,
    },
  },
];

const EMPTY: PricingConfigBody = {
  contract: "dynamic",
  fixed_eur_per_kwh: null,
  supplier_margin_eur_per_kwh: 0.025,
  tax_eur_per_kwh: 0.0831,
  vat_pct: 0.21,
  export_mode: "saldering",
  sun_bonus_pct: 0.0,
  export_margin_eur_per_kwh: 0.0,
  feed_in_tariff_eur_per_kwh: 0.08,
};

export function PricingSettings() {
  const [state, setState] = useState<PricingConfigResponse | null>(null);
  const [form, setForm] = useState<PricingConfigBody>(EMPTY);
  const [presetId, setPresetId] = useState<string>("custom");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const [taxIncluded, setTaxIncluded] = useState<boolean>(getPriceIncludesTax());
  const [chartRes, setChartRes] = useState<ChartResolution>(getChartResolution());

  function onToggleTaxIncluded(v: boolean) {
    setTaxIncluded(v);
    setPriceIncludesTax(v);
  }

  function onToggleQuarterHour(v: boolean) {
    // `v=true` means "show 15-min bars", false means hourly mean.
    const next: ChartResolution = v ? "15min" : "1h";
    setChartRes(next);
    setChartResolution(next);
  }

  useEffect(() => {
    fetchPricingConfig()
      .then((s) => {
        setState(s);
        if (s.effective) {
          setForm(s.effective);
          setPresetId(matchPreset(s.effective) ?? "custom");
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  function applyPreset(id: string) {
    setPresetId(id);
    if (id === "custom") return; // keep current values, just open the form
    const p = PRESETS.find((x) => x.id === id);
    if (p) setForm(p.body);
  }

  async function onSave() {
    setBusy(true);
    setError(null);
    try {
      const s = await savePricingConfig(form);
      setState(s);
      setSavedFlash("Opgeslagen");
      window.setTimeout(() => setSavedFlash(null), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setBusy(true);
    setError(null);
    try {
      const s = await resetPricingConfig();
      setState(s);
      if (s.effective) {
        setForm(s.effective);
        setPresetId(matchPreset(s.effective) ?? "custom");
      }
      setSavedFlash("Teruggezet naar standaard");
      window.setTimeout(() => setSavedFlash(null), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const sourceLabel =
    state?.source === "override"
      ? "Aangepast"
      : state?.source === "default"
        ? "Standaard van installateur"
        : "Niet ingesteld";

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-xs uppercase tracking-wider text-ink-mute">
        Energiecontract
      </h2>

      <div className="card p-4 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-ink-mute">Huidige bron</div>
          <div className="text-sm font-medium">{sourceLabel}</div>
        </div>

        {/* Preset chips */}
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <PresetChip
              key={p.id}
              label={p.label}
              active={presetId === p.id}
              onClick={() => applyPreset(p.id)}
            />
          ))}
          <PresetChip
            label="Aangepast"
            active={presetId === "custom"}
            onClick={() => applyPreset("custom")}
          />
        </div>

        {/* Contract type */}
        <div className="flex gap-3 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.contract === "dynamic"}
              onChange={() =>
                setForm({ ...form, contract: "dynamic", fixed_eur_per_kwh: null })
              }
            />
            Dynamisch (EPEX + marge + belasting)
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={form.contract === "fixed"}
              onChange={() =>
                setForm({
                  ...form,
                  contract: "fixed",
                  fixed_eur_per_kwh: form.fixed_eur_per_kwh ?? 0.32,
                })
              }
            />
            Vast tarief
          </label>
        </div>

        {/* Import-side fields */}
        <div className="grid grid-cols-2 gap-3">
          {form.contract === "fixed" ? (
            <Field
              label="Vast tarief"
              suffix="€/kWh"
              value={form.fixed_eur_per_kwh ?? 0}
              onChange={(v) => setForm({ ...form, fixed_eur_per_kwh: v })}
            />
          ) : (
            <Field
              label="Marge leverancier (excl. BTW)"
              suffix="€/kWh"
              value={form.supplier_margin_eur_per_kwh}
              onChange={(v) =>
                setForm({ ...form, supplier_margin_eur_per_kwh: v })
              }
            />
          )}
          <Field
            label="Energiebelasting (excl. BTW)"
            suffix="€/kWh"
            value={form.tax_eur_per_kwh}
            onChange={(v) => setForm({ ...form, tax_eur_per_kwh: v })}
          />
        </div>

        {/* Export-side block - what the customer is paid per kWh sent
            back to the grid. Two modes; only show the relevant fields. */}
        <div className="border-t border-line pt-3 flex flex-col gap-2">
          <div className="text-xs uppercase tracking-wider text-ink-mute">
            Teruglevering
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={form.export_mode === "saldering"}
                onChange={() => setForm({ ...form, export_mode: "saldering" })}
              />
              Salderen (NL t/m 2026)
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={form.export_mode === "dynamic"}
                onChange={() => setForm({ ...form, export_mode: "dynamic" })}
              />
              Dynamisch (= inkoopprijs)
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={form.export_mode === "flat"}
                onChange={() => setForm({ ...form, export_mode: "flat" })}
              />
              Vast bedrag
            </label>
          </div>
          {form.export_mode === "flat" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Terugleververgoeding"
                suffix="€/kWh"
                value={form.feed_in_tariff_eur_per_kwh}
                onChange={(v) =>
                  setForm({ ...form, feed_in_tariff_eur_per_kwh: v })
                }
              />
            </div>
          ) : form.export_mode === "saldering" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Zonnebonus (overdag)"
                suffix="%"
                value={form.sun_bonus_pct * 100}
                onChange={(v) =>
                  setForm({ ...form, sun_bonus_pct: v / 100 })
                }
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Zonnebonus (overdag)"
                suffix="%"
                value={form.sun_bonus_pct * 100}
                onChange={(v) =>
                  setForm({ ...form, sun_bonus_pct: v / 100 })
                }
              />
              <Field
                label="Verkoopvergoeding"
                suffix="€/kWh"
                value={form.export_margin_eur_per_kwh}
                onChange={(v) =>
                  setForm({ ...form, export_margin_eur_per_kwh: v })
                }
                allowNegative
              />
            </div>
          )}
          {form.export_mode === "saldering" && (
            <div className="text-[10px] text-ink-mute leading-snug">
              U ontvangt per teruggeleverde kWh dezelfde prijs die u
              betaalt voor afgenomen stroom (saldering).
              {form.sun_bonus_pct > 0 && (
                <>
                  {" "}Bonus van {(form.sun_bonus_pct * 100).toFixed(0)}% tussen
                  zonsopkomst en zonsondergang.
                </>
              )}
            </div>
          )}
          {form.export_mode === "dynamic" && (
            <div className="text-[10px] text-ink-mute leading-snug">
              U ontvangt de inkoopprijs (EPEX) per teruggeleverde kWh.
              {form.sun_bonus_pct > 0 && (
                <>
                  {" "}Bonus van {(form.sun_bonus_pct * 100).toFixed(0)}% tussen
                  zonsopkomst en zonsondergang.
                </>
              )}
              {form.export_margin_eur_per_kwh < 0 && (
                <>
                  {" "}De verkoopvergoeding wordt afgetrokken van wat u krijgt.
                </>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="text-sm text-danger">{error}</div>
        )}

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            disabled={busy || state?.source !== "override"}
            onClick={onReset}
            className="text-sm text-ink-mute disabled:opacity-40 hover:text-ink"
          >
            Terug naar standaard
          </button>
          <div className="flex items-center gap-3">
            {savedFlash && (
              <span className="text-xs text-accent">{savedFlash}</span>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={onSave}
              className="bg-accent text-white text-sm rounded-full px-4 py-2 font-medium disabled:opacity-60"
            >
              Opslaan
            </button>
          </div>
        </div>

        {/* Display preference - applies to the Prijzen tab's chart.
            Only meaningful for dynamic contracts; harmless on fixed
            (the chart shows wholesale either way for those). */}
        <div className="border-t border-line pt-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium">
              Toon prijzen inclusief belasting
            </div>
            <div className="text-xs text-ink-mute mt-0.5">
              Voegt energiebelasting en leveringsmarge toe aan de
              groothandelsprijs op de Prijzen-tab. Alleen zinvol bij
              een dynamisch contract.
            </div>
          </div>
          <Toggle checked={taxIncluded} onChange={onToggleTaxIncluded} />
        </div>

        {/* Bucket-size preference - drives the Prijzen chart's bar
            granularity. NL day-ahead has been 15-min MTU since 2025;
            Tibber bills per quarter and shows quarter bars. Most other
            NL suppliers still display hourly means, which is also our
            default. Earnings calc stays hourly either way (the math
            doesn't change). */}
        <div className="border-t border-line pt-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium">
              Tijdsblokken van 15 minuten in de prognose
            </div>
            <div className="text-xs text-ink-mute mt-0.5">
              Toon de prijzen onder het batterijplan per kwartier in
              plaats van per uur. De Prijzen-tab staat altijd op
              kwartieren - zo publiceert de NL day-ahead-markt sinds 2025
              en zo rekenen leveranciers inmiddels af.
            </div>
          </div>
          <Toggle
            checked={chartRes === "15min"}
            onChange={onToggleQuarterHour}
          />
        </div>
      </div>
    </section>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex shrink-0 h-6 w-11 rounded-full transition-colors ${
        checked ? "bg-accent" : "bg-line"
      }`}
    >
      <span
        className={`inline-block w-5 h-5 my-0.5 rounded-full bg-card shadow-sm transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function PresetChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 text-xs rounded-full transition-colors ${
        active
          ? "bg-accent text-white font-medium"
          : "bg-card-soft text-ink-mute hover:text-ink"
      }`}
    >
      {label}
    </button>
  );
}

function Field({
  label,
  value,
  suffix,
  onChange,
  allowNegative = false,
}: {
  label: string;
  value: number;
  suffix: string;
  onChange: (v: number) => void;
  /** Tibber-style sales fees are negative numbers - opt-in so the
   *  bulk of fields (tax / margin / etc.) still get the `min=0` guard. */
  allowNegative?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-ink-mute">{label}</span>
      <div className="flex items-center gap-1 bg-card-soft rounded-lg px-2 py-1.5">
        <input
          type="number"
          step="0.001"
          {...(allowNegative ? {} : { min: 0 })}
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            onChange(Number.isFinite(v) ? v : 0);
          }}
          className="flex-1 min-w-0 bg-transparent text-sm font-medium focus:outline-none"
        />
        <span className="text-xs text-ink-mute">{suffix}</span>
      </div>
    </label>
  );
}

/** Identify which preset (if any) matches the saved config exactly. */
function matchPreset(body: PricingConfigBody): string | null {
  for (const p of PRESETS) {
    const b = p.body;
    if (
      b.contract === body.contract &&
      b.supplier_margin_eur_per_kwh === body.supplier_margin_eur_per_kwh &&
      b.tax_eur_per_kwh === body.tax_eur_per_kwh &&
      b.vat_pct === body.vat_pct &&
      b.export_mode === body.export_mode &&
      b.sun_bonus_pct === body.sun_bonus_pct &&
      b.export_margin_eur_per_kwh === body.export_margin_eur_per_kwh &&
      // feed_in_tariff only matters in flat mode; if both are flat
      // require it to match too, else ignore.
      (b.export_mode === "dynamic" ||
        b.feed_in_tariff_eur_per_kwh === body.feed_in_tariff_eur_per_kwh)
    ) {
      return p.id;
    }
  }
  return null;
}
